import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  Calendar,
  CheckCircle,
  FileText,
  Lock,
  Receipt,
  Trash2,
} from "lucide-react";

import {
  Button as ButtonRaw,
  Card,
  CardHeader as CardHeaderRaw,
  Spinner,
  StatusPill as StatusPillRaw,
  colors,
  spacing,
  typography,
} from "../../design-system";
import { backButtonStyle, iconWrapStyle } from "./styles";
import { formatCurrency, formatDate } from "./helpers";
import { usePayRunDetail } from "./usePayRunDetail";
import { usePayRunActions } from "./usePayRunActions";

import ErrorBlock from "./components/ErrorBlock";
import SummaryStat from "./components/SummaryStat";
import DetailRow from "./components/DetailRow";
import PayStubsTable from "./components/PayStubsTable";
import ConfirmActionModal from "./components/ConfirmActionModal";

import type { ConfirmKind, PayRunStatus, TabType } from "./types";

const Button = ButtonRaw as any;
const CardHeader = CardHeaderRaw as any;
const StatusPill = StatusPillRaw as any;

export default function PayRunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading: loading, error, refetch } = usePayRunDetail(id);
  const [tab, setTab] = useState<TabType>("overview");
  const [confirm, setConfirm] = useState<ConfirmKind>(null);

  const {
    processing,
    actionError,
    finalize,
    voidRun,
    deleteRun,
  } = usePayRunActions({
    id,
    onFinished: () => setConfirm(null),
    onNavigateAway: () => navigate("/payroll/runs"),
  });

  if (loading) {
    return (
      <div
        style={{
          background: colors.bgPage,
          minHeight: "100%",
          padding: spacing[10] + "px",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            textAlign: "center",
            paddingTop: spacing[12],
          }}
        >
          <Spinner size={20} label="Loading pay run..." inline />
        </div>
      </div>
    );
  }

  const errMsg = error instanceof Error ? error.message : null;

  if (errMsg || !data) {
    return (
      <div
        style={{
          background: colors.bgPage,
          minHeight: "100%",
          fontFamily: typography.fontFamily,
          padding: spacing[6] + "px " + spacing[8] + "px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ width: "100%" }}>
          <button
            onClick={() => navigate("/payroll/runs")}
            style={{ ...backButtonStyle, marginBottom: spacing[6] }}
          >
            <ArrowLeft size={16} /> Pay runs
          </button>
          <ErrorBlock
            title="Could not load pay run"
            message={errMsg || "Pay run not found"}
          />
          <Button variant="secondary" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const { run, stubs } = data;
  const status = (String(run.status || "draft").toLowerCase() as PayRunStatus);
  const canFinalize = status === "calculated" || status === "draft";
  const canDelete = status === "calculated" || status === "draft";
  const canVoid = status === "finalized";
  const isVoided = status === "voided";

  return (
    <div
      style={{
        background: colors.bgPage,
        minHeight: "100%",
        fontFamily: typography.fontFamily,
        padding: spacing[6] + "px " + spacing[8] + "px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ width: "100%" }}>
        <button
          onClick={() => navigate("/payroll/runs")}
          style={{ ...backButtonStyle, marginBottom: spacing[3] }}
        >
          <ArrowLeft size={16} /> Pay runs
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: spacing[4],
            flexWrap: "wrap",
            marginBottom: spacing[8],
          }}
        >
          <div style={{ flex: 1, minWidth: 280 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacing[3],
                marginBottom: spacing[1],
              }}
            >
              <h1
                style={{
                  ...typography.displaySm,
                  color: colors.textPrimary,
                  margin: 0,
                }}
              >
                {formatDate(run.pay_period_start)} to{" "}
                {formatDate(run.pay_period_end)}
              </h1>
              <StatusPill status={status} />
            </div>
            <p
              style={{
                ...typography.body,
                color: colors.textSecondary,
                margin: 0,
              }}
            >
              Pay date {formatDate(run.pay_date)}
              {run.employee_count !== undefined && (
                <>
                  {" · "}
                  {run.employee_count} employees
                </>
              )}
              {run.country && (
                <>
                  {" · "}
                  {run.country}
                  {run.subnational ? " (" + run.subnational + ")" : ""}
                </>
              )}
            </p>
          </div>
          <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
            {canDelete && (
              <Button
                variant="ghost"
                onClick={() => setConfirm("delete")}
                iconLeft={<Trash2 size={14} />}
              >
                Delete
              </Button>
            )}
            {canFinalize && (
              <Button
                variant="primary"
                onClick={() => setConfirm("finalize")}
                iconLeft={<Lock size={14} />}
              >
                Finalize
              </Button>
            )}
            {canVoid && (
              <Button
                variant="secondary"
                onClick={() => setConfirm("void")}
                iconLeft={<Ban size={14} />}
              >
                Void
              </Button>
            )}
          </div>
        </div>

        {actionError && (
          <ErrorBlock title="Action failed" message={actionError} />
        )}

        {isVoided && (
          <Card
            style={{
              background: colors.bgCardActive,
              border: "1px dashed " + colors.borderDefault,
              marginBottom: spacing[5],
              display: "flex",
              alignItems: "center",
              gap: spacing[3],
            }}
          >
            <Ban size={18} color={colors.textMuted} />
            <div
              style={{ ...typography.bodySm, color: colors.textSecondary }}
            >
              This pay run was voided. It cannot be edited or finalized.
            </div>
          </Card>
        )}

        <Card style={{ marginBottom: spacing[5] }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: spacing[6],
            }}
          >
            <SummaryStat
              label="Total gross"
              value={formatCurrency(run.total_gross, run.currency)}
            />
            <SummaryStat
              label="Deductions"
              value={formatCurrency(
                run.total_employee_deductions,
                run.currency
              )}
            />
            <SummaryStat
              label="Employer cost"
              value={formatCurrency(
                run.total_employer_contributions,
                run.currency
              )}
            />
            <SummaryStat
              label="Total net"
              value={formatCurrency(run.total_net, run.currency)}
              emphasis
            />
          </div>
        </Card>

        <div
          style={{
            display: "flex",
            gap: spacing[6],
            borderBottom: "1px solid " + colors.borderDefault,
            marginBottom: spacing[5],
          }}
        >
          {[
            {
              key: "overview" as const,
              label: "Overview",
              icon: <FileText size={15} />,
            },
            {
              key: "stubs" as const,
              label: "Pay stubs (" + stubs.length + ")",
              icon: <Receipt size={15} />,
            },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  background: "none",
                  border: "none",
                  padding: spacing[3] + "px 0",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  ...typography.bodyMd,
                  fontWeight: active ? 600 : 500,
                  color: active ? colors.textPrimary : colors.textSecondary,
                  borderBottom: active
                    ? "2px solid " + colors.brandPrimary
                    : "2px solid transparent",
                  marginBottom: -1,
                  fontFamily: typography.fontFamily,
                  transition: "color 150ms ease, border-color 150ms ease",
                }}
              >
                {t.icon}
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: spacing[5],
            }}
          >
            <Card>
              <CardHeader
                title="Run details"
                icon={
                  <div style={iconWrapStyle}>
                    <Calendar size={18} color={colors.brandPrimary} />
                  </div>
                }
              />
              <div>
                <DetailRow
                  label="Pay period start"
                  value={formatDate(run.pay_period_start)}
                />
                <DetailRow
                  label="Pay period end"
                  value={formatDate(run.pay_period_end)}
                />
                <DetailRow label="Pay date" value={formatDate(run.pay_date)} />
                <DetailRow
                  label="Country"
                  value={
                    (run.country || "CA") +
                    (run.subnational ? " (" + run.subnational + ")" : "")
                  }
                />
                {run.created_at && (
                  <DetailRow
                    label="Created"
                    value={formatDate(run.created_at)}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: spacing[3] + "px 0",
                  }}
                >
                  <div
                    style={{
                      ...typography.bodySm,
                      color: colors.textSecondary,
                    }}
                  >
                    Status
                  </div>
                  <StatusPill status={status} />
                </div>
              </div>
            </Card>
            <Card>
              <CardHeader
                title="Totals breakdown"
                icon={
                  <div style={iconWrapStyle}>
                    <CheckCircle size={18} color={colors.brandPrimary} />
                  </div>
                }
              />
              <div>
                <DetailRow
                  label="Gross pay"
                  value={formatCurrency(run.total_gross, run.currency)}
                />
                <DetailRow
                  label="Employee deductions"
                  value={formatCurrency(
                    run.total_employee_deductions,
                    run.currency
                  )}
                />
                <DetailRow
                  label="Net pay"
                  value={formatCurrency(run.total_net, run.currency)}
                />
                <DetailRow
                  label="Employer contributions"
                  value={formatCurrency(
                    run.total_employer_contributions,
                    run.currency
                  )}
                />
                <DetailRow
                  label="Remittance owed"
                  value={formatCurrency(
                    run.total_remittance_owed,
                    run.currency
                  )}
                />
              </div>
            </Card>
          </div>
        )}

        {tab === "stubs" && (
          <PayStubsTable
            stubs={stubs}
            runId={String(id)}
            currency={run.currency}
          />
        )}
      </div>

      <ConfirmActionModal
        open={confirm === "finalize"}
        title="Finalize this pay run?"
        message="Once finalized, this run is locked and the pay stubs become a source of truth for T4s, ROEs, and remittance reports. You cannot edit hours or amounts afterwards."
        confirmLabel="Yes, finalize"
        processingLabel="Finalizing..."
        processing={processing}
        onCancel={() => setConfirm(null)}
        onConfirm={finalize}
      />

      <ConfirmActionModal
        open={confirm === "void"}
        title="Void this pay run?"
        message="Voiding marks this run inactive. The pay stubs remain in the audit trail but are excluded from current totals. This cannot be reverted."
        confirmLabel="Yes, void"
        processingLabel="Voiding..."
        processing={processing}
        onCancel={() => setConfirm(null)}
        onConfirm={voidRun}
      />

      <ConfirmActionModal
        open={confirm === "delete"}
        title="Delete this pay run?"
        message="This pay run and its pay stubs will be permanently removed. This cannot be undone. Only drafts and unfinalized runs can be deleted."
        confirmLabel="Yes, delete"
        processingLabel="Deleting..."
        processing={processing}
        onCancel={() => setConfirm(null)}
        onConfirm={deleteRun}
      />
    </div>
  );
}